import { useEffect, useLayoutEffect, useRef, useState } from "react";

const MARGIN_SCREENS = 1.5;
const callbacks = new Map();
const heightByKind = new Map();
let observer = null;

const watch = (el, callback) => {
  if (!observer) {
    observer = new window.IntersectionObserver(
      (entries) => entries.forEach((entry) => callbacks.get(entry.target)?.(entry.isIntersecting)),
      { rootMargin: `${MARGIN_SCREENS * 100}% 0px` },
    );
  }
  callbacks.set(el, callback);
  observer.observe(el);
  return () => {
    callbacks.delete(el);
    if (!observer) return;
    observer.unobserve(el);
    if (callbacks.size === 0) {
      observer.disconnect();
      observer = null;
    }
  };
};

const widthResets = new Set();
let lastWidth = 0;

const onResize = () => {
  if (window.innerWidth === lastWidth) return;
  lastWidth = window.innerWidth;
  widthResets.forEach((reset) => reset());
};

const onWidthChange = (reset) => {
  if (widthResets.size === 0) {
    lastWidth = window.innerWidth;
    window.addEventListener("resize", onResize);
  }
  widthResets.add(reset);
  return () => {
    widthResets.delete(reset);
    if (widthResets.size === 0) window.removeEventListener("resize", onResize);
  };
};

const isNearViewport = (el) => {
  const { top, bottom } = el.getBoundingClientRect();
  const margin = window.innerHeight * MARGIN_SCREENS;
  return bottom >= -margin && top <= window.innerHeight + margin;
};

export const LazyCard = ({ children, className, ...rest }) => {
  const ref = useRef(null);
  const supported = typeof window.IntersectionObserver === "function";
  const [mode, setMode] = useState(supported ? "pending" : "live");
  const [height, setHeight] = useState(null);
  const [floor, setFloor] = useState(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => onWidthChange(() => setFloor(null)), []);

  useLayoutEffect(() => {
    if (!supported) return;
    const el = ref.current;

    const sleep = (measured) => {
      if (measured > 0) setFloor(measured);
      const next = measured > 0 ? measured : heightByKind.get(className);
      if (!next) return setMode("live");
      setHeight(next);
      setMode("idle");
    };

    if (isNearViewport(el)) setMode("live");
    else sleep(0);

    return watch(el, (isIntersecting) => {
      if (el.closest("[hidden]")) return;
      if (isIntersecting) setMode("live");
      else if (modeRef.current === "live") sleep(el.getBoundingClientRect().height);
    });
  }, [supported, className]);

  useLayoutEffect(() => {
    if (mode !== "live") return;
    const measured = ref.current.getBoundingClientRect().height;
    if (measured > 0) heightByKind.set(className, measured);
  });

  const style = mode === "idle" && height ? { height } : mode === "live" && floor ? { minHeight: floor } : undefined;

  return (
    <div ref={ref} className={className} {...rest} style={style}>
      {mode === "live" ? children : null}
    </div>
  );
};
